import { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  effectiveTheme: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Initialize from localStorage or default to system
    const stored = globalThis.localStorage?.getItem("theme") as Theme;
    return stored || "system";
  });

  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(() => {
    return globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  // Calculate effective theme
  const effectiveTheme = theme === "system" ? systemTheme : theme;

  // Update HTML class and localStorage when theme changes
  useEffect(() => {
    const root = globalThis.document?.documentElement;
    if (root) {
      root.classList.remove("light", "dark");
      root.classList.add(effectiveTheme);
    }
    globalThis.localStorage?.setItem("theme", theme);
  }, [theme, effectiveTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = globalThis.matchMedia?.("(prefers-color-scheme: dark)");
    const handler = (e: { matches: boolean }) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    mediaQuery?.addEventListener("change", handler);
    return () => mediaQuery?.removeEventListener("change", handler);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, effectiveTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
